import GradientBlue from "../../../Components/InputFields/GradientBlue";
import LogoWhite10 from "../../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../../Media/PNG/LogoBlue.svg";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectFromNext } from "../../../helper/nextRedirect";
import Back from "./Back";
import Input from "./Input";
import BirthDatePicker from "./BirthDatePicker";
import { API_BASE_URL } from "../../../config/api";

const DEFAULT_SIGNUP_DATA = {
  fn: "",
  ln: "",
  national_id: "",
  birth_date: "",
};

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

function toEnglishDigits(value) {
  return String(value || "")
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)));
}

function normalizeNationalId(value) {
  return toEnglishDigits(value).replace(/\D/g, "").slice(0, 10);
}

function isValidNationalId(value) {
  const nationalId = normalizeNationalId(value);

  if (!nationalId) return true;
  return /^\d{10}$/.test(nationalId);
}

function omitFalsyFields(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => Boolean(value)),
  );
}

export default function ContainerSignUpInfo({
  text,
  signupData,
  setSignupData,
  setStep,
}) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [localSignupData, setLocalSignupData] = useState(DEFAULT_SIGNUP_DATA);
  const formData = signupData || localSignupData;
  const updateSignupData = setSignupData || setLocalSignupData;
  const navigate = useNavigate();
  const { search } = useLocation();
  const nextParam = new URLSearchParams(search).get("next");
  const redirectFromNext = getRedirectFromNext(nextParam);
  const signinPath = nextParam
    ? `/signin/email?next=${encodeURIComponent(nextParam)}`
    : "/signin/email";
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [invalidNationalId, setInvalidNationalId] = useState(false);
  const [signupNotOkay, setSignupNotOkay] = useState(false);

  const [loading, setLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false)


  useEffect(() => {
    if (redirectFromNext) {
      localStorage.setItem("post_login_redirect", redirectFromNext);
    }
  }, [redirectFromNext]);

  const handleFnChange = (e) => {
    updateSignupData((previousData) => ({
      ...DEFAULT_SIGNUP_DATA,
      ...previousData,
      fn: e.target.value,
    }));
    setFieldsRequired(false);
  };
  const handleLnChange = (e) => {
    updateSignupData((previousData) => ({
      ...DEFAULT_SIGNUP_DATA,
      ...previousData,
      ln: e.target.value,
    }));
    setFieldsRequired(false);
  };
  const handleNationalIdChange = (e) => {
    const nationalId = normalizeNationalId(e.target.value);

    updateSignupData((previousData) => ({
      ...DEFAULT_SIGNUP_DATA,
      ...previousData,
      national_id: nationalId,
    }));
    setInvalidNationalId(false);
  };
  const handleBirthDateChange = (birthDate) => {
    updateSignupData((previousData) => ({
      ...DEFAULT_SIGNUP_DATA,
      ...previousData,
      birth_date: birthDate,
    }));
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    const normalizedNationalId = normalizeNationalId(formData.national_id);
    const trimmedFn = (formData.fn || "").trim();
    const trimmedLn = (formData.ln || "").trim();
    const { referal_code, ...formDataWithoutTypo } = formData;
    const signupPayload = {
      ...formDataWithoutTypo,
      fn: trimmedFn,
      ln: trimmedLn,
      national_id: normalizedNationalId,
      referral_code: (formData.referral_code || referal_code || "").trim(),
    };
    const requestPayload = omitFalsyFields(signupPayload);

    updateSignupData((previousData) => ({ ...previousData, ...signupPayload }));

    if (!trimmedFn || !trimmedLn) {
      setFieldsRequired(true);
      return;
    }

    setFieldsRequired(false);

    if (!isValidNationalId(normalizedNationalId)) {
      setInvalidNationalId(true);
      return;
    }

    setInvalidNationalId(false);
    setSignupNotOkay(false);
    setLoading(true);
    setPassLoading(false)
    try {
      let response = await fetch(
        `${API_BASE_URL}auth/trader/signup/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestPayload),
        },
      );

      // Now handle final response (either first or retried)
      if (!response.ok) {
        setSignupNotOkay(true);
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json().catch(() => null);
      console.log("Account Data:", data);
      setLoading(false);
      setPassLoading(true)
      setTimeout(() => navigate(signinPath),3000)
      
    } catch (error) {
      console.error("Error fetching account data:", error);
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          {text}
        </h1>

        <Back onClick={() => setStep(2)} />
      </div>

      <div>
        <form className="flex flex-col gap-5" onSubmit={handleSignin}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 mb-5">
            <Input
              label="نام"
              isNecessary={true}
              value={formData.fn || ""}
              type="text"
              id="fn"
              name="fn"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleFnChange}
            />
            <Input
              label="نام خانوادگی"
              isNecessary={true}
              value={formData.ln || ""}
              type="text"
              id="ln"
              name="ln"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleLnChange}
            />
            <Input
              label="کد ملی"
              value={formData.national_id || ""}
              type="text"
              id="national_id"
              name="national_id"
              isReadOnly={false}
              isAuotComplete={true}
              isLTR={true}
              isNumeric={true}
              onChange={handleNationalIdChange}
            />
            <BirthDatePicker
              label="تاریخ تولد"
              value={formData.birth_date || ""}
              isReadOnly={false}
              onChange={handleBirthDateChange}
            />
          </div>
          {!loading && !passLoading ? (
            <GradientBlue
              type="submit"
              value="ایجاد"
              onClick={handleSignin}
            />
          ) : loading && !passLoading ?(
            <GradientBlue
              type="submit"
              isLoader={true}
              onClick={() => {}}
            />
          ) : <GradientBlue
              type="submit"
              haveLottie={true}
              onClick={() => {}}
            />}
        </form>
        {fieldsRequired && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            لطفا نام و نام خانوادگی را وارد کنید
          </p>
        )}
        {invalidNationalId && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            کد ملی وارد شده صحیح نمی باشد
          </p>
        )}
        {signupNotOkay && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            ثبت نام انجام نشد. لطفاً دوباره تلاش کنید.
          </p>
        )}
      </div>
    </div>
  );
}
